import { FoodsSelect, FoodsTextInput, HelperBox } from "@foodpilot/foods";
import { Checkbox, FormControlLabel, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionPlanForm, ActionPlanFormScoreOptions } from "./useActionPlanForm";
import { GoalBlockList } from "./GoalBlock";
import { ScoreWithVariation } from "src/services/ScoreService";
import { isCampaignBefore, useCampaigns } from "src/context/Campaigns/useCampaigns";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { Fragment } from "react/jsx-runtime";
import { ACTION_PLAN, DELETE, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { Loading } from "src/components/utils/Loading";
import { useState } from "react";
import { DeleteButton } from "../../DeleteModal/DeleteButton";
import { DeleteModal } from "../../DeleteModal/DeleteModal";

type ActionPlanInitFormProps = {
    form: ActionPlanForm;
    setForm: (newValue: ActionPlanForm) => void;
    closeModal: () => void;
    remainingScoreOptions: ActionPlanFormScoreOptions;
    allScoreOptions: ScoreWithVariation[];
};
export const ActionPlanInitForm = (props: ActionPlanInitFormProps) => {
    const { form, setForm, remainingScoreOptions } = props;

    const [isDeletionOpen, setDeletionOpen] = useState(false);
    const { isActionPlanSimplified } = useApplicationParameters();
    const theme = useTheme();
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const { getElligibleStartingPosts } = useActionsContext();

    const elligibleStartingPosts = getElligibleStartingPosts();
    const selectedElligiblePost = elligibleStartingPosts?.find((item) => item.id === form.startingPostId);

    const filterOptions =
        selectedElligiblePost?.campaign !== undefined ? { startFilter: selectedElligiblePost?.campaign } : {};

    const { campaigns: futureCampaigns } = useCampaigns({
        filterOptions: filterOptions,
    });

    const isSimplified = isActionPlanSimplified();
    const isAdvanced = isSimplified === false;

    return (
        <Stack flexDirection={"column"} gap={theme.spacing(4)} padding={theme.spacing(4)}>
            <Stack gap={theme.spacing(2)}>
                <Typography variant="h5">{t("actionplan.trajectory.form.startingyear")}</Typography>
                {elligibleStartingPosts !== undefined ?
                    <FoodsSelect
                        selectedOption={selectedElligiblePost ?? null}
                        options={elligibleStartingPosts}
                        onChange={(selectedElligiblePost) => {
                            const selectedId = selectedElligiblePost?.id;
                            if (selectedId === undefined) return;

                            const selectedStartingDate = selectedElligiblePost?.campaign?.startDate ?? "";
                            const selectedEndDate = form.targetYear?.startDate ?? "";
                            const doesChoiceResetEnd = isCampaignBefore({
                                startDate: selectedEndDate,
                                endDate: selectedStartingDate,
                                canBeEqual: false,
                            });

                            setForm({
                                ...form,
                                targetYear: doesChoiceResetEnd ? null : form.targetYear,
                                startingPostId: selectedId,
                            });
                        }}
                        getId={(item) => item.id}
                        getName={(item) => item.campaign?.label ?? "No name"}
                        closeOnSelect
                        disableSearch
                        textOptions={{
                            placeholder: t("component.select.placeholder"),
                            searchPlaceholder: t("component.select.searchplaceholder"),
                            emptyOptionsText: t("component.select.nooptions"),
                            defaultValueText: t("component.select.defaultvalue"),
                            noResult: t("component.select.noresultfound"),
                        }}
                        BoxProps={{ width: "200px" }}
                    />
                :   <Loading />}
                <HelperBox>
                    <Typography variant="body-medium">{t("actionplan.trajectory.form.startingyear.hint")}</Typography>
                </HelperBox>
            </Stack>
            <Stack gap={theme.spacing(1)}>
                <Typography variant="h5">{t("actionplan.trajectory.form.targetyear")}</Typography>
                {isSimplified && form.targetYear === null ?
                    <Stack gap={theme.spacing(1)}>
                        <HelperBox>
                            <Typography variant="body-medium">{t("action_plan.notargetcampaign")}</Typography>
                        </HelperBox>
                    </Stack>
                :   <FoodsSelect
                        selectedOption={form.targetYear}
                        options={futureCampaigns}
                        onChange={(selectedCampaign) => {
                            setForm({
                                ...form,
                                targetYear: selectedCampaign,
                            });
                        }}
                        disabled={isSimplified}
                        getId={(campaign) => campaign.id}
                        getName={(campaign) => campaign.label}
                        closeOnSelect
                        disableSearch
                        textOptions={{
                            placeholder: t("component.select.placeholder"),
                            searchPlaceholder: t("component.select.searchplaceholder"),
                            emptyOptionsText: t("component.select.nooptions"),
                            defaultValueText: t("component.select.defaultvalue"),
                            noResult: t("component.select.noresultfound"),
                        }}
                        BoxProps={{ width: "200px" }}
                    />
                }
            </Stack>
            {isAdvanced && (
                <Fragment>
                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="h5">{t("actionplan.trajectory.form.growth")}</Typography>
                        <Stack flexDirection={"row"} gap={theme.spacing(4)}>
                            <FoodsTextInput
                                ExtraInputProps={{
                                    type: "number",
                                    sx: {
                                        maxWidth: "200px",
                                    },
                                }}
                                value={form.increasePerYear}
                                unit={"%"}
                                onChange={(newValue) => {
                                    setForm({
                                        ...form,
                                        increasePerYear: newValue,
                                    });
                                }}
                                disabled={form.isGrowthDisabled}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.isGrowthDisabled}
                                        onChange={(e) => {
                                            setForm({
                                                ...form,
                                                isGrowthDisabled: !form.isGrowthDisabled,
                                            });
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body-medium">
                                        {t("actionplan.trajectory.form.nogrowthfactor")}
                                    </Typography>
                                }
                            />
                        </Stack>
                    </Stack>
                    <GoalBlockList
                        remainingScoreOptions={remainingScoreOptions}
                        goals={form.reductionGoals}
                        setGoals={(newGoals) => {
                            setForm({
                                ...form,
                                reductionGoals: newGoals,
                            });
                        }}
                    />
                </Fragment>
            )}
            {form.id !== null && canUser(DELETE, ACTION_PLAN) && (
                <Fragment>
                    <DeleteButton setOpen={setDeletionOpen} />
                    <DeleteModal
                        isOpen={isDeletionOpen}
                        setOpen={setDeletionOpen}
                        onSuccess={() => {
                            props.closeModal();
                        }}
                        actionPlanId={form.id}
                    />
                </Fragment>
            )}
        </Stack>
    );
};
